/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { IconButton } from '@fluentui/react/lib/Button';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { Link } from '@fluentui/react/lib/Link';
import { IStackProps, IStackSlots, Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { CluEntityCombinationOnboardingModal } from 'src/clu/appShell/cluEntityDetailsPage/components/CluEntityCombinationOnboardingModal';
import { CluEntityCombinationOptionCards } from 'src/clu/appShell/cluEntityDetailsPage/components/CluEntityCombinationOptionCards';
import { overlapMethodsDetails } from 'src/clu/appShell/cluEntityDetailsPage/components/cluEntityCombinationOptionsDetails';
import { BladeModel } from 'src/core/components/BladeModel';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { LuisCommandBar } from 'src/core/components/LuisCommandBar';
import { LuisText } from 'src/core/components/LuisText';
import { StackBladeHost } from 'src/core/components/StackBladeHost';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { EntityStore } from 'src/core/types/EntityStore';
import { EntityCompositionSetting } from 'src/core/types/EntitySwagger';
import { tryParseJson } from 'src/core/utils/base';
import { getStorage } from 'src/core/utils/getStorage';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';

export type CluEntityCombinationPivotProps = {
    entityStore: EntityStore;
    isSaveOperationRunning: boolean;
    selectedCombinationOption: EntityCompositionSetting;
    onEntityComponentClick: () => void;
    setSelectedCombinationOption: (option: EntityCompositionSetting) => void;
};

const messages = defineMessages({
    helpPanelHeader: { id: 'clu.cluEntityCombinationPivot.helpPanelHeader', defaultMessage: 'Overlap methods' },
    learnMore: { id: 'clu.cluEntityCombinationPivot.learnMore', defaultMessage: 'Learn about overlap methods' },
    entityComponents: { id: 'clu.cluEntityCombinationPivot.entityComponents', defaultMessage: 'entity components' },
    overlapPanelDescription: {
        id: 'clu.cluEntityCombinationPivot.overlapPanelDescription',
        defaultMessage: 'Select an overlap method to learn more about when to use it and how it works.'
    },
    description: {
        id: 'clu.cluEntityCombinationPivot.description',
        defaultMessage: `When multiple components define an entity, their predictions can share some of the same text. When this happens, combining components will determine the final prediction for the entity.`
    },
    emptyMessage: {
        id: 'clu.cluEntityCombinationPivot.emptyMessage',
        defaultMessage: 'There are no entity components to set an overlap method for. You can add some by going to'
    }
});

const headerTheme = getScopedTheme('GlobalHeaderBlades');
const mainBladeContentContainerStyles = classNamesFunction<IStackProps, IComponentStyles<IStackSlots>>()({
    root: {
        minHeight: 140,
        boxShadow: '0px 0.3px 0.9px rgba(0, 0, 0, 0.1), 0px 1.6px 3.6px rgba(0, 0, 0, 0.13)'
    }
});

/**
 * Component displaying and managing the entity combination options
 */
export const CluEntityCombinationPivot = observer((props: CluEntityCombinationPivotProps) => {
    const { entityStore, isSaveOperationRunning, selectedCombinationOption, setSelectedCombinationOption, onEntityComponentClick } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const { serviceName } = getPortalFromUrl();
    const currentProject = rootStore.currentCluProjectStore;
    const hasMultipleComponents = entityStore.capabilities(currentProject.labelsPerEntity.get(entityStore.name)).length > 1;
    const [showTutorial, setShowTutorial] = React.useState(false);
    const [selectedHelpDetails, setSelectedHelpDetails] = React.useState<EntityCompositionSetting | 'main'>(null);

    const commandBarItems: ICommandBarItemProps[] = [
        {
            key: 'learnMore',
            iconProps: { iconName: 'Info' },
            text: localize(messages.learnMore),
            onClick: () => {
                setSelectedHelpDetails('main');
                logButtonClick(serviceName, 'Schema', 'LearnOverlapMethods', 'EntityOptions', currentProject.id);
            }
        }
    ];

    React.useEffect(() => {
        setShowTutorial(
            !tryParseJson<boolean>(getStorage('localStorage').getData(LuisConstants.StorageKeys.CluEntityOverlapMethodTutorialSeen)) ?? true
        );
    }, []);

    const getBladeContent = () => {
        switch (selectedHelpDetails) {
            case 'main':
                return [
                    <LuisText>{localize(messages.overlapPanelDescription)}</LuisText>,
                    ...overlapMethodsDetails()
                        .filter(
                            method =>
                                selectedCombinationOption === method.methodKey ||
                                LuisConstants.Clu.HiddenEntityOptions.every(methodKey => methodKey !== method.methodKey)
                        )
                        .map(method => (
                            <Stack
                                horizontal
                                verticalAlign="center"
                                horizontalAlign="space-between"
                                styles={mainBladeContentContainerStyles}
                                tokens={{ padding: '12px 8px 12px 15px' }}>
                                <Stack tokens={{ childrenGap: 12 }}>
                                    <LuisText fontWeight={600} variant="mediumPlus">
                                        {method.title}
                                    </LuisText>
                                    <LuisText>{method.briefedDescriptionText}</LuisText>
                                </Stack>
                                <IconButton
                                    iconProps={{ iconName: 'ChevronRightMed', title: method.title }}
                                    onClick={() => setSelectedHelpDetails(method.methodKey)}
                                />
                            </Stack>
                        ))
                ];
            case 'combineComponents':
            case 'separateComponents':
            case 'requireExactOverlap':
            case 'returnLongestOverlap':
                const method = overlapMethodsDetails({ goToMethodDocumentation: setSelectedHelpDetails }).find(
                    m => m.methodKey === selectedHelpDetails
                );

                return (
                    <Stack tokens={{ childrenGap: 24 }}>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
                            <IconButton aria-label="back" iconProps={{ iconName: 'Back' }} onClick={() => setSelectedHelpDetails('main')} />
                            <LuisText variant="large">{method.title}</LuisText>
                        </Stack>
                        {method.methodDocumentation}
                    </Stack>
                );
            default:
        }
    };

    const blades: BladeModel[] = [
        {
            key: 'helpBladeId',
            header: (
                <LuisText styles={{ root: { paddingLeft: 8 } }} fontWeight={600} variant="xLarge">
                    {localize(messages.helpPanelHeader)}
                </LuisText>
            ),
            content: <Stack tokens={{ childrenGap: 12, padding: '0 8px' }}>{getBladeContent()}</Stack>
        }
    ];

    const keyboardFocus = (helpDetails: EntityCompositionSetting | 'main') => {
        let dom: HTMLElement;
        if (helpDetails === 'main') {
            dom = document.querySelector('.ms-Button--commandBar.ms-CommandBarItem-link');
        }
        if (helpDetails === 'combineComponents') {
            dom = document.querySelectorAll('[data-automation-id="cardContainer"]')[0].querySelector('.ms-Button.ms-Button--icon');
        }
        if (helpDetails === 'separateComponents') {
            dom = document.querySelectorAll('[data-automation-id="cardContainer"]')[1].querySelector('.ms-Button.ms-Button--icon');
        }
        dom?.focus();
    };

    return (
        <Stack verticalFill tokens={{ childrenGap: 20, padding: '0 0 12px 0' }}>
            <LuisCommandBar items={commandBarItems} borderPosition="Bottom" />
            <LuisText>{localize(messages.description)}</LuisText>

            {hasMultipleComponents ? (
                <CluEntityCombinationOptionCards
                    onOptionClick={setSelectedCombinationOption}
                    isSaveOperationRunning={isSaveOperationRunning}
                    onLearnMethodMoreClick={setSelectedHelpDetails}
                    entityCombinationOption={selectedCombinationOption}
                />
            ) : (
                <EmptyStateView
                    message={
                        <LuisText>
                            {localize(messages.emptyMessage)}&nbsp;
                            <Link onClick={onEntityComponentClick}>{localize(messages.entityComponents)}</Link>
                        </LuisText>
                    }
                />
            )}

            <StackBladeHost
                blades={blades}
                topSpacing={headerTheme.topSpacing}
                isOpen={Boolean(selectedHelpDetails)}
                onDismiss={() => {
                    setSelectedHelpDetails(null);
                    keyboardFocus(selectedHelpDetails);
                }}
                onLastBladeDismissed={() => {
                    setSelectedHelpDetails(null);
                    keyboardFocus(selectedHelpDetails);
                }}
            />

            <CluEntityCombinationOnboardingModal hidden={!showTutorial} onDismiss={() => setShowTutorial(false)} />
        </Stack>
    );
});
